import { ReportFilters, ReportSortBy, ReportSortDir, useReportData } from '../../../contexts/ReportsContextProvider'
import styles from './InstructorReport.module.css'
import genericTable from './GenericTable.module.css'
import { useUser } from '../../../contexts/userContext'
import { t } from 'i18next'
import { Button } from '../../../common/components/button/Button'
import { useCallback, useMemo, useState } from 'react'
import { getIcon } from '../../../common/components/icons/utils'
import classNames from 'classnames'
import { CompareGameData, InstructorReportGameData } from '../../../contexts/ReportsContextHelper'
import { PaginationSelectOption } from '../../../composites/GamesOverview/components/PaginationControls'
import { SingleValue } from 'react-select'
import { ReportFiltersForm } from './ReportFiltersForm'
import { createPDFfromHTML, HeaderSorter, SummaryDataBox } from './ReportCommon'
import reportStyles from './ReportCommon.module.css'
import { ReportingGameCard } from '../../../composites/GamesOverview/components/card/ReportingGameCard'
import { StatusPill } from '../../../common/components/StatusPill/StatusPill'
import { DateTime, GameStatus } from '../../../types/commonTypes'
import PricePink from '../../../assets/images/price_pink.svg'
import { CellElement, Table, TableRow } from './GenericTable'
import { formatTime } from '../../../util/number'
import { dateTimeToDashDateString, parseDDMMYYYY, parseFinnishDateTimeToString } from '../../../util/string'

const MetricTitles = [
  t('report.compare.created', 'Created at'),
  t('report.compare.players_teams_total', 'Players/Teams total'),
  t('report.compare.players_teams_started', 'Players/Teams started'),
  t('report.compare.players_teams_completed', 'Players/Teams completed'),
  t('report.compare.avg_time', 'Avg. game duration'),
  t('report.compare.completion_rate', 'Completion rate'),
]

const MetricKeys: (keyof CompareGameData)[] = [
  'createdAt',
  'playersTotalCount',
  'playersStartedCount',
  'playersCompletedCount',
  'avgTime',
  'completionRate',
]

export const InstructorReport: React.FC = () => {
  const {
    instructorReportSummaryData,
    pageData,
    currentPage,
    pageSize,
    allPageOptions,
    setPage,
    lastPage,
    next,
    prev,
    selectPageSize,
    loading,
    setFilters,
    setSort,
    fetchCompareGamesData,
    compareGameData,
  } = useReportData()
  const { user } = useUser()
  const [activeSortCol, setActiveSortCol] = useState<ReportSortBy>()
  const [activeSortDir, setActiveSortDir] = useState<ReportSortDir>()
  const [selectedGames, setSelectedGames] = useState<number[]>([])
  const [isComparing, setIsComparing] = useState(false)
  const [rangeSet, setRangeSet] = useState(false)

  const setFiltersInternal = useCallback(
    (filters: ReportFilters) => {
      setRangeSet(!!filters.createdAtFrom || !!filters.createdAtUntil)
      setFilters(filters)
    },
    [setFilters],
  )

  const openCompareView = useCallback(() => {
    setIsComparing(true)
    if (fetchCompareGamesData) fetchCompareGamesData(selectedGames)
  }, [fetchCompareGamesData, selectedGames])

  const closeCompareView = useCallback(() => {
    setSelectedGames([])
    setIsComparing(false)
  }, [])

  const handleSort = useCallback(
    (sortKey: string) => (direction: ReportSortDir | string) => {
      if (setSort) {
        setActiveSortCol(sortKey as ReportSortBy)
        setActiveSortDir(direction as ReportSortDir)
        setSort(sortKey as ReportSortBy, direction as ReportSortDir)
      }
    },
    [setSort],
  )

  const onChangePageSize = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      selectPageSize(option!.value)
    },
    [selectPageSize],
  )

  const onChangeCurrentPage = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      setPage(option!.value)
    },
    [setPage],
  )

  const toggleSelected = useCallback(
    (id: number) => {
      if (selectedGames.includes(id)) setSelectedGames((prev) => prev.filter((p) => p !== id))
      else if (selectedGames.length < 3) setSelectedGames((prev) => prev.concat(id))
    },
    [selectedGames],
  )

  const tableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []
    pageData?.forEach((dataRow) => {
      const row = dataRow as InstructorReportGameData
      const tableRow: CellElement[] = [
        row.id,
        <>
          <div>{row.name}</div>
          <div className={styles.creator}>{row.creator}</div>
        </>,

        dateTimeToDashDateString(parseDDMMYYYY(row.createdAt.toString())),
        parseFinnishDateTimeToString(row.lastAnswerReceived.toString()),
        <StatusPill
          status={
            row.openStatus ? GameStatus.IN_PROGRESS : row.numberOfAnswers > 0 ? GameStatus.ARCHIVED : GameStatus.DRAFT
          }
        />,
        row.numberOfTeams,
      ]
      rows.push(tableRow)
    })
    return rows
  }, [pageData])

  return (
    <div className={classNames(styles.container, loading && reportStyles.loading)}>
      {isComparing ? (
        <CompareView
          onClose={closeCompareView}
          selectedGames={selectedGames}
          compareGameData={compareGameData}
          pageData={pageData as InstructorReportGameData[]}
        />
      ) : (
        <>
          <div className={styles.headerRow}>
            <div className={styles.welcomeBox}>
              <div className={styles.welcomeLeft}>
                <h2>{t('reports.reports_hi', 'Hi %{name}!', { name: user?.contactName || user?.name })}</h2>
                <div className={styles.basicGreyText}>
                  {t('reports.instructor_overview', "Here's your Seppo activity overview.")}
                </div>
              </div>
              <div className={styles.welcomeRight}>
                <div className={styles.pinkBubbleSmall} />
                <div className={styles.pinkBubbleBig} />
                <img className={styles.pinkTrophy} src={PricePink} alt={''} />
              </div>
            </div>
          </div>
          <ReportFiltersForm onChange={setFiltersInternal} />

          <div className={reportStyles.summaryDataBoxesRow}>
            <SummaryDataBox
              iconName='gameController'
              value={instructorReportSummaryData?.totalGames}
              label={t('reports.games_created', 'Games Created')}
              toolTipText={
                rangeSet
                  ? t('reports.games_created_instructor_tooltip_range', 'Created games within date range')
                  : t('reports.games_created_tooltip_instructor', 'Games created')
              }
            />
            <SummaryDataBox
              iconName='multipleUsers'
              value={instructorReportSummaryData?.totalTeams}
              label={t('reports.total_players', 'Total Players')}
              toolTipText={
                rangeSet
                  ? t('reports.total_players_tooltip_range', 'Total players participating in these games')
                  : t('reports.total_players_tooltip', 'Total players participating in these games')
              }
            />
            <SummaryDataBox
              iconName='play'
              value={instructorReportSummaryData?.totalPlayed}
              label={t('reports.games_played', 'Games Played')}
              toolTipText={
                rangeSet
                  ? t('reports.games_played_tooltip_range', 'Games played within date range')
                  : t('reports.games_played_tooltip', 'Games played')
              }
            />
          </div>
          <h2>{t('reports.top_played_games', 'Top Played Games')}</h2>
          {instructorReportSummaryData && (
            <div className={reportStyles.summaryDataBoxesRow}>
              {instructorReportSummaryData?.topGames.map((game, index) => {
                return <ReportingGameCard game={game} topPlayedPosition={index + 1} key={index} />
              })}
            </div>
          )}

          <div className={styles.tableTitleRow}>
            <h2>{t('reports.my_games', 'My Games')}</h2>
            <div className={styles.topComparisonButtons}>
              <Button
                variant='outline-tiny'
                onClick={() => setSelectedGames([])}
                className={classNames(styles.unselectButton, selectedGames.length === 0 && styles.hiddenButton)}
              >
                {t('reports.unselect_all', 'Unselect all')}
              </Button>
              <Button
                variant='tiny'
                onClick={() => (selectedGames.length > 1 ? openCompareView() : () => {})}
                className={classNames(
                  styles.compareButton,
                  selectedGames.length < 1 && styles.hiddenButton,
                  selectedGames.length < 2 && styles.dimmedButton,
                )}
              >
                {t('reports.to_compare_games', 'Compare selected games')}
              </Button>
            </div>
          </div>
          <Table
            isSortable
            isSelectable
            hasPagination
            headerWidths={['30%', '17%', '22%', '17%', '12%']}
            headerLabels={[
              t('reports.table.name', 'Name'),
              t('reports.table.created', 'Created'),
              t('reports.table.last_updated', 'Last updated'),
              t('reports.table.status', 'Status'),
              t('reports.table.players', 'Players'),
            ]}
            headerSortTags={[
              ReportSortBy.IR_GAME_NAME,
              ReportSortBy.IR_CREATED_AT,
              ReportSortBy.IR_LAST_ANSWER_RECEIVED,
              ReportSortBy.IR_STATUS,
              ReportSortBy.IR_PLAYERS,
            ]}
            onSort={handleSort}
            activeSortCol={activeSortCol}
            activeSortDir={activeSortDir}
            onToggleRowSelected={toggleSelected}
            selectedRows={selectedGames}
            rows={tableRowData}
            currentPage={currentPage}
            pageSize={pageSize}
            lastPage={lastPage}
            allPageOptions={allPageOptions}
            onClickNext={next}
            onClickPrev={prev}
            onChangePageSize={onChangePageSize}
            onChangeCurrentPage={onChangeCurrentPage}
            itemsPerPageLabel={t('reports.pagination.games_per_page', 'games per page')}
            loading={loading}
          />

          <div className={styles.comparisonButtons}>
            <Button
              variant='outline-normal'
              onClick={() => setSelectedGames([])}
              className={classNames(styles.unselectButton, selectedGames.length === 0 && styles.hiddenButton)}
            >
              {t('reports.unselect_all', 'Unselect all')}
            </Button>
            <Button
              onClick={() => (selectedGames.length > 1 ? openCompareView() : () => {})}
              className={classNames(
                styles.compareButton,
                selectedGames.length < 1 && styles.hiddenButton,
                selectedGames.length < 2 && styles.dimmedButton,
              )}
            >
              {t('reports.to_compare_games', 'Compare selected games')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export type CompareViewProps = {
  selectedGames: number[]
  compareGameData?: CompareGameData[]
  pageData?: InstructorReportGameData[]
  onClose: () => void
}

const getValueString = (key: keyof CompareGameData, value: string | number | DateTime | undefined): string => {
  return key === 'avgTime' && typeof value === 'number'
    ? formatTime(value)
    : key === 'completionRate'
    ? value + '%'
    : value + ''
}

export const CompareView: React.FC<CompareViewProps> = ({ selectedGames, compareGameData, pageData, onClose }) => {
  const [hideShadows, setHideShadows] = useState(false)
  const handleDownloadPDF = useCallback(() => {
    setHideShadows(true)
    setTimeout(() => {
      createPDFfromHTML('PDF_DIV', 'Game_Comparison')
      setHideShadows(false)
    }, 25)
  }, [])

  return (
    <div className={classNames(styles.compareView, hideShadows && styles.hideShadows)} id='PDF_DIV'>
      <div className={styles.compareHeaderRow}>
        <div onClick={onClose} className={styles.compareBack}>
          {getIcon('arrowLeft')}
        </div>
        <h2>{t('reports.game_comparison_title', 'Game Comparison')}</h2>
        <Button className={styles.downloadPdfButton} variant='outline-normal' onClick={handleDownloadPDF}>
          {t('reports.download_pdf', 'Export as PDF')}
        </Button>
      </div>
      <div className={reportStyles.summaryDataBoxesRow}>
        {compareGameData?.map((game, index) => {
          return (
            <ReportingGameCard
              key={index}
              game={{
                id: game.id,
                thumbnailUrl: game.thumbnailUrl,
                gameName: game.name,
                modifiedAt: game.updatedAt.split('T')[0].split('/').reverse().join('-'),
                playerInfo: game.playersTotalCount,
              }}
              topPlayedPosition={0}
            />
          )
        })}
      </div>
      {compareGameData && (
        <table className={classNames(genericTable.genericTable, genericTable.noPagination, genericTable.noSort)}>
          <colgroup>
            <col style={{ width: '30%' }} />
            {selectedGames.length > 2 ? (
              <>
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
              </>
            ) : (
              <>
                <col style={{ width: '35%' }} />
                <col style={{ width: '35%' }} />
              </>
            )}
          </colgroup>
          <thead>
            <HeaderSorter label={t('reports.table.metric', 'Metric')} />
            <HeaderSorter label={compareGameData[0].name} />
            <HeaderSorter label={compareGameData[1].name} />
            {compareGameData[2] && <HeaderSorter label={compareGameData[2].name} />}
          </thead>
          <tbody>
            {MetricTitles.map((title, index) => {
              return (
                <tr key={index}>
                  <td>{title}</td>
                  <td>{getValueString(MetricKeys[index], compareGameData[0][MetricKeys[index]])}</td>
                  <td>{getValueString(MetricKeys[index], compareGameData[1][MetricKeys[index]])}</td>
                  {compareGameData[2] && (
                    <td>{getValueString(MetricKeys[index], compareGameData[2][MetricKeys[index]])}</td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
